import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UserService } from 'src/app/services/user/user.service';
import { forkJoin } from 'rxjs';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { User } from 'src/app/classes/User';
import { BookedLessonByChild } from 'src/app/classes/BookedLessonByChild';
import { Child } from 'src/app/classes/Child';
import {
  ChildAdminService,
  ChildWritePayload,
} from 'src/app/services/child/child-admin.service';
import { ConfirmDialogComponent } from 'src/app/components/dialog/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-user-children-info',
  templateUrl: './user-children-info.component.html',
  styleUrls: ['./user-children-info.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('accordionAnimation', [
      state('closed', style({ height: '0px', opacity: 0, overflow: 'hidden' })),
      state('open', style({ height: '*', opacity: 1 })),
      transition('closed <=> open', [animate('300ms ease-in-out')]),
    ]),
  ],
})
export class UserChildrenInfoComponent implements OnInit {
  @Input() user: User;

  public userBookedLessons: BookedLessonByChild[] = [];

  public pageNumbers;
  public totalElements = 0;
  public pageSizes = [10, 20, 50];
  public pageSize = this.pageSizes[0];
  public loading: boolean;
  public expandedRowIndex: number | null = null;

  public saving = false;
  public errorMessage: string;
  public showAddForm = false;
  public editingChildId: number | null = null;
  private listNeedsRefresh = false;

  public childForm: FormGroup;

  /** When true, API uses `/v1/children` (JWT user), not the selected customer row. */
  get stockChildrenApi(): boolean {
    return this.childAdminService.usesStockChildrenResource;
  }

  constructor(
    private activeModal: NgbActiveModal,
    private userService: UserService,
    private childAdminService: ChildAdminService,
    private modalService: NgbModal,
    private formBuilder: FormBuilder
  ) {
    this.childForm = this.formBuilder.group({
      firstname: ['', Validators.required],
      lastname: ['', Validators.required],
      age: [null as number | null, [Validators.required, Validators.min(0)]],
      allergy: [''],
    });
  }

  ngOnInit(): void {
    if (!this.user.children) {
      this.user.children = [];
    }
    this.reloadBookedLessons();
  }

  public reloadBookedLessons(): void {
    if (!this.user.children?.length) {
      this.userBookedLessons = [];
      this.loading = false;
      return;
    }
    this.loading = true;
    const queries = this.user.children.map((child) =>
      this.userService.getUserBookedLessonsByChildId(child.id, 9999)
    );
    forkJoin(queries).subscribe({
      next: (responsesArray) => {
        this.user.children.forEach((child, index) => {
          this.userBookedLessons[child.id] = responsesArray[index];
        });
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  closeModal(sendData: unknown): void {
    this.activeModal.close(sendData);
  }

  /** Close icon: refresh customer list if children were changed. */
  requestClose(): void {
    this.activeModal.close(this.listNeedsRefresh ? 'updated' : 'dismiss');
  }

  toggleRow(index: number): void {
    this.expandedRowIndex = this.expandedRowIndex === index ? null : index;
  }

  openAddForm(): void {
    this.cancelEditForm();
    this.showAddForm = true;
    this.childForm.reset({ firstname: '', lastname: '', age: null, allergy: '' });
  }

  startEdit(child: Child): void {
    this.showAddForm = false;
    this.editingChildId = child.id;
    this.childForm.patchValue({
      firstname: child.firstname,
      lastname: child.lastname,
      age: child.age,
      allergy: child.allergy ?? '',
    });
  }

  cancelEditForm(): void {
    this.showAddForm = false;
    this.editingChildId = null;
    this.childForm.reset();
    this.errorMessage = null;
  }

  private payloadFromForm(): ChildWritePayload {
    const v = this.childForm.getRawValue();
    return {
      firstname: v.firstname.trim(),
      lastname: v.lastname.trim(),
      age: Number(v.age),
      allergy: v.allergy?.trim() ? v.allergy.trim() : null,
    };
  }

  saveNewChild(): void {
    this.childForm.markAllAsTouched();
    if (this.childForm.invalid) {
      return;
    }
    this.saving = true;
    this.errorMessage = null;
    this.childAdminService
      .addChild(this.user.id, this.payloadFromForm())
      .subscribe({
        next: (created) => {
          this.listNeedsRefresh = true;
          this.user.children = [...(this.user.children || []), new Child(created)];
          this.cancelEditForm();
          this.saving = false;
          this.reloadBookedLessons();
        },
        error: (err) => {
          this.saving = false;
          this.errorMessage = err?.error?.error ?? err?.message ?? 'Request failed';
        },
      });
  }

  saveEditChild(): void {
    if (this.editingChildId == null) {
      return;
    }
    this.childForm.markAllAsTouched();
    if (this.childForm.invalid) {
      return;
    }
    this.saving = true;
    this.errorMessage = null;
    this.childAdminService
      .updateChild(this.user.id, this.editingChildId, this.payloadFromForm())
      .subscribe({
        next: (updated) => {
          this.listNeedsRefresh = true;
          const idx = this.user.children.findIndex((c) => c.id === updated.id);
          if (idx >= 0) {
            this.user.children[idx] = new Child(updated);
          }
          this.cancelEditForm();
          this.saving = false;
          this.reloadBookedLessons();
        },
        error: (err) => {
          this.saving = false;
          this.errorMessage = err?.error?.error ?? err?.message ?? 'Request failed';
        },
      });
  }

  confirmDeleteChild(child: Child, event: Event): void {
    event.stopPropagation();
    const dialog = this.modalService.open(ConfirmDialogComponent, {
      scrollable: true,
      windowClass: 'modal-confirm',
      size: 'md',
      centered: true,
    });
    dialog.componentInstance.message = `Delete child ${child.firstname} ${child.lastname}?`;
    dialog.result.then(
      (result) => {
        if (result?.status === 'confirmed') {
          this.deleteChild(child);
        }
      },
      () => {}
    );
  }

  private deleteChild(child: Child): void {
    this.saving = true;
    this.errorMessage = null;
    this.childAdminService.deleteChild(this.user.id, child.id).subscribe({
      next: () => {
        this.listNeedsRefresh = true;
        this.user.children = this.user.children.filter((c) => c.id !== child.id);
        delete this.userBookedLessons[child.id];
        if (this.editingChildId === child.id) {
          this.cancelEditForm();
        }
        this.saving = false;
      },
      error: (err) => {
        this.saving = false;
        this.errorMessage = err?.error?.error ?? err?.message ?? 'Request failed';
      },
    });
  }

  rowClick(index: number, event: Event): void {
    const el = event.target as HTMLElement;
    if (el.closest('button, a, input, textarea, select')) {
      return;
    }
    this.toggleRow(index);
  }
}
